import { useQuery, UseQueryOptions } from 'react-query';
import client from 'client';
import { DateSpan, SchedulingPreferences, SchedulingPreferencesData } from './types';
import { addMinutes } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { groupTimeIntervalsByZonedDay } from '../../UserSettings/SchedulingPreferencesForm/dateUtils';
import { getCurrentTimeZoneId } from '../../../meetings/schedule/timeZoneId';

const parseIntervalTime = (time: string) => {
    const [hours, minutes] = time.split(':').slice(0, 2);
    return 60 * Number(hours) + Number(minutes) || 0;
};

const localTimeIntervalToDate = (
    dateStamp: string,
    startTimeStamp: string,
    endTimeStamp: string
) => {
    const baseDate = toDate(`${dateStamp}T00:00:00Z`);
    // allows us to parse the string '24:00' to mean the next day's 00:00
    return {
        start: addMinutes(baseDate, parseIntervalTime(startTimeStamp)),
        end: addMinutes(baseDate, parseIntervalTime(endTimeStamp)),
    };
};

// convert intervals to a flat list of start/end Dates
const schedulingHoursToUTCDateIntervals = (
    schedulingHours: SchedulingPreferencesData['schedulingHours'] = []
): DateSpan[] =>
    schedulingHours.flatMap(intervalSet =>
        intervalSet.intervals.map(({ start, end }) =>
            localTimeIntervalToDate(intervalSet.date, start, end)
        )
    );

// for react-query select function
// convert to DateSpan format and regroup by the timezoneId from the preferences
const transformSchedulingHoursData = (data: SchedulingPreferencesData): SchedulingPreferences => {
    const schedulingHoursSorted = data.schedulingHours.sort(
        (a, b) => +new Date(a.date) - +new Date(b.date)
    );
    return {
        ...data,
        schedulingHoursBase: schedulingHoursSorted,
        schedulingHours: groupTimeIntervalsByZonedDay(
            schedulingHoursToUTCDateIntervals(schedulingHoursSorted),
            getCurrentTimeZoneId()
        ),
    };
};
export const fetchSchedulingPreferences = async (
    eventId: number
): Promise<SchedulingPreferencesData> => {
    const endpoint = `/api/v2/meetings/events/${eventId}/meetings/schedulingPreferences`;
    const response = await client().get<SchedulingPreferencesData>(endpoint);
    return response.data;
};

export const useSchedulingPreferences = (
    eventId: number,
    options?: UseQueryOptions<SchedulingPreferencesData, Error, SchedulingPreferences>
) => {
    return useQuery<SchedulingPreferencesData, Error, SchedulingPreferences>(
        ['schedulingPreferences', eventId],
        () => fetchSchedulingPreferences(eventId),
        {
            select: transformSchedulingHoursData,
            ...options,
        }
    );
};

export default useSchedulingPreferences;
